import React, { useState, useCallback } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { BaseAccountForm } from './BaseAccountForm';
import { AccountFormField } from '../ui/AccountFormField';
import { ValidationResult } from '../../utils/validation';
import { usePasswordReset } from '../../hooks/usePasswordReset';
import { useTheme } from '../../contexts/ThemeContext';
import { isNativeApp } from '../../utils/platform';
import { Mail } from 'lucide-react';

export interface ForgotPasswordFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  className?: string;
}

/**
 * Forgot password form component
 * Allows users to request a password reset email
 */
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSuccess,
  onError,
  className
}) => {
  const [email, setEmail] = useState('');
  const [emailValidation, setEmailValidation] = useState<ValidationResult>({ isValid: true });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetCounter, setCaptchaResetCounter] = useState(0);
  const { theme } = useTheme();

  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '0x4AAAAAAB9PVF5WNM2_V9pV';

  const {
    requestReset,
    isSubmitting,
    error,
    success,
    clearError,
    clearSuccess
  } = usePasswordReset();

  // Validate email
  const validateEmail = useCallback((email: string): ValidationResult => {
    if (!email.trim()) {
      return {
        isValid: false,
        error: 'Email is required'
      };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        error: 'Please enter a valid email address'
      };
    }

    return { isValid: true };
  }, []);

  // Update email value
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);

    // Clear messages when user starts typing
    if (error) clearError();
    if (success) clearSuccess();
  }, [error, success, clearError, clearSuccess]);

  const skipCaptcha = isNativeApp();

  // Check if form is valid
  const isFormValid = () => {
    return email.trim() !== '' && emailValidation.isValid && (skipCaptcha || !!captchaToken);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    try {
      await requestReset(email, skipCaptcha ? undefined : captchaToken!);
      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to request password reset';
      onError(errorMessage);
    } finally {
      setCaptchaToken(null);
      setCaptchaResetCounter(prev => prev + 1);
    }
  };

  return (
    <BaseAccountForm
      title="Forgot Password"
      description="Enter your email address and we'll send you a link to reset your password."
      icon={<Mail className="w-5 h-5" />}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitText="Send Reset Link"
      submitDisabled={!isFormValid()}
      error={error}
      success={success}
      onClearError={clearError}
      onClearSuccess={clearSuccess}
      className={className}
    >
      <div className="space-y-6">
        {/* Email Field */}
        <AccountFormField
          id="reset-email"
          label="Email Address"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onValidation={setEmailValidation}
          validator={validateEmail}
          placeholder="Enter your email address"
          required
          autoComplete="email"
          helperText="We'll send a password reset link to this email address"
        />

        {/* CAPTCHA */}
        {!skipCaptcha && (
          <div className="flex justify-center">
            <Turnstile
              key={captchaResetCounter}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setCaptchaToken(token)}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
              options={{ theme: theme === 'dark' ? 'dark' : 'light' }}
            />
          </div>
        )}
      </div>
    </BaseAccountForm>
  );
};
